import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { PDFDocument } from "pdf-lib";
import { useHistory } from "react-router-dom";
import { addDoc, collection, doc, getDoc, getDocs, orderBy, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { getBlob, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import Navbar from "../Components/Navbar/navbar";
import useEstadoSalvamento from "../Utils/useEstadoSalvamento";
import { auth, db, storage } from "../Config/firebase";
import RelatorioModuloElasticidadeMultiploPrint from "./RelatorioModuloElasticidadeMultiploPrint";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";

const COLECAO_RELATORIOS = "tblRelatoriosModuloElasticidade";
const TIPO_LAUDO_MODULO_ELASTICIDADE = "Relatório Módulo de Elasticidade";

function nomeSeguroArquivo(valor) {
  return String(valor || "relatorio-modulo-elasticidade")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

async function gerarPdfRelatorioModuloElasticidade(certificado) {
  if (document.fonts?.ready) await document.fonts.ready;

  const container = document.querySelector(".relatorio-modulo-multiplo");
  const paginas = Array.from(
    container?.querySelectorAll(".pagina:not(.pagina-pdf-certificado-modulo)") || []
  );

  if (!paginas.length) {
    throw new Error("As páginas do relatório ainda não estão prontas.");
  }

  const imagens = Array.from(container.querySelectorAll("img"));
  await Promise.all(imagens.map((imagem) => {
    if (imagem.complete) return Promise.resolve();
    return new Promise((resolve) => {
      imagem.addEventListener("load", resolve, { once: true });
      imagem.addEventListener("error", resolve, { once: true });
    });
  }));

  const pdfFinal = await PDFDocument.create();

  for (const pagina of paginas) {
    const canvas = await html2canvas(pagina, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
      logging: false,
    });
    const pdfPagina = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
    pdfPagina.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, 210, 297, undefined, "FAST");
    const origem = await PDFDocument.load(pdfPagina.output("arraybuffer"));
    const [paginaCopiada] = await pdfFinal.copyPages(origem, [0]);
    pdfFinal.addPage(paginaCopiada);
  }

  if (certificado?.url || certificado?.storagePathAtual) {
    const bytesCertificado = certificado.storagePathAtual
      ? await (await getBlob(ref(storage, certificado.storagePathAtual))).arrayBuffer()
      : await (await fetch(certificado.url)).arrayBuffer();
    const pdfCertificado = await PDFDocument.load(bytesCertificado);
    const paginasCertificado = await pdfFinal.copyPages(pdfCertificado, pdfCertificado.getPageIndices());
    paginasCertificado.forEach((pagina) => pdfFinal.addPage(pagina));
  }

  return new Blob([await pdfFinal.save()], { type: "application/pdf" });
}

function dataAtualISO() {
  const data = new Date();
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, "0");
  const dia = String(data.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
}

function formatarDataBR(data) {
  if (!data) return "-";
  const [ano, mes, dia] = String(data).split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : String(data);
}

function ordenarModulos(lista) {
  return [...lista].sort((a, b) => String(b.codigo || "").localeCompare(String(a.codigo || ""), "pt-BR", { numeric: true }));
}

function documentoEhCertificadoPadrao(documento) {
  return (
    documento.equipamentoId &&
    documento.storagePathAtual &&
    documento.statusDocumento === "ENVIADO" &&
    documento.tipoDocumentoCodigo === "CERTIFICADO_DE_CALIBRACAO"
  );
}

function documentoEhCertificado(documento) {
  const tipoCodigo = String(documento.tipoDocumentoCodigo || "").toUpperCase();
  const tipoNome = String(documento.tipoDocumentoNome || "").toUpperCase();

  return (
    documento.equipamentoId &&
    documento.storagePathAtual &&
    (
      documento.numeroCertificado ||
      tipoCodigo.includes("CERTIFICADO") ||
      tipoNome.includes("CERTIFICADO")
    )
  );
}

export default function RelatoriosModuloElasticidade() {
  const { salvandoCadastro, executarSalvamento } = useEstadoSalvamento();
  const history = useHistory();
  const [obras, setObras] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [relatorios, setRelatorios] = useState([]);
  const [certificados, setCertificados] = useState([]);
  const [todosCertificados, setTodosCertificados] = useState([]);
  const [mostrarTodosCertificados, setMostrarTodosCertificados] = useState(false);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [obraSelecionada, setObraSelecionada] = useState(null);
  const [selecionados, setSelecionados] = useState([]);
  const [todos, setTodos] = useState(false);
  const [certificadoSelecionado, setCertificadoSelecionado] = useState(null);
  const [modoPrint, setModoPrint] = useState(false);
  const [modulosPrint, setModulosPrint] = useState([]);
  const [certificadoPrint, setCertificadoPrint] = useState(null);
  const [dataRelatorioPrint, setDataRelatorioPrint] = useState("");
  const [autoPrint, setAutoPrint] = useState(false);
  const [relatorioPrint, setRelatorioPrint] = useState(null);

  useEffect(() => { carregarDados(); }, []);

  async function carregarDados() {
    const [obrasSnap, modulosSnap, relatoriosSnap, equipamentosSnap, documentosSnap] = await Promise.all([
      getDocs(consultaTenantAtual(query(collection(db, "obras"), orderBy("nome", "asc")))),
      getDocs(consultaTenantAtual(query(collection(db, "tblModulosElasticidade"), orderBy("criadoEm", "desc")))),
      getDocs(consultaTenantAtual(query(collection(db, COLECAO_RELATORIOS), orderBy("criadoEm", "desc")))),
      getDocs(consultaTenantAtual(collection(db, "equipamentos"))),
      getDocs(consultaTenantAtual(collection(db, "eq_documentos"))),
    ]);

    setObras(obrasSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setModulos(modulosSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setRelatorios(relatoriosSnap.docs.map((item) => ({ id: item.id, ...item.data() })));

    const documentos = documentosSnap.docs.map((item) => ({ id: item.id, ...item.data() }));
    const montarListaCertificados = async (usarTodos) => {
      const lista = await Promise.all(equipamentosSnap.docs.map(async (item) => {
        const equipamento = { id: item.id, ...item.data() };
        const certificado = documentos.find((documento) =>
          documento.equipamentoId === equipamento.id &&
          (usarTodos ? documentoEhCertificado(documento) : documentoEhCertificadoPadrao(documento))
        );
        if (!certificado?.storagePathAtual) return null;
        try {
          return {
            id: `${equipamento.id}-${certificado.id}`,
            equipamentoId: equipamento.id,
            certificadoId: certificado.id,
            equipamentoNome: equipamento.nome || "Equipamento",
            numeroCertificado: certificado.numeroCertificado || "",
            nomeArquivoOriginal: certificado.nomeArquivoOriginal || "",
            storagePathAtual: certificado.storagePathAtual,
            url: await getDownloadURL(ref(storage, certificado.storagePathAtual)),
          };
        } catch (error) { return null; }
      }));

      return lista
        .filter(Boolean)
        .sort((a, b) => String(a.equipamentoNome || "").localeCompare(String(b.equipamentoNome || ""), "pt-BR"));
    };

    setCertificados(await montarListaCertificados(false));
    setTodosCertificados(await montarListaCertificados(true));
  }

  const certificadosExibidos = useMemo(
    () => (mostrarTodosCertificados ? todosCertificados : certificados),
    [certificados, mostrarTodosCertificados, todosCertificados]
  );

  const ensaiosDaObra = useMemo(
    () => ordenarModulos(modulos.filter((modulo) => obraSelecionada && modulo.obraId === obraSelecionada.id)),
    [modulos, obraSelecionada]
  );

  function abrirGeracao() {
    setObraSelecionada(null);
    setSelecionados([]);
    setTodos(false);
    setCertificadoSelecionado(null);
    setMostrarTodosCertificados(false);
    setDialogAberto(true);
  }

  function selecionarObra(obra) {
    setObraSelecionada(obra);
    setSelecionados([]);
    setTodos(false);
  }

  function alternarTodos(marcado) {
    setTodos(marcado);
    setSelecionados(marcado ? ensaiosDaObra : []);
  }

  function alterarSelecionados(valor) {
    setSelecionados(valor);
    setTodos(valor.length > 0 && valor.length === ensaiosDaObra.length);
  }

  function alternarEnsaio(ensaio, marcado) {
    const novosSelecionados = marcado
      ? [...selecionados.filter((item) => item.id !== ensaio.id), ensaio]
      : selecionados.filter((item) => item.id !== ensaio.id);

    alterarSelecionados(novosSelecionados);
  }

  async function gerarRelatorio() {
    if (!obraSelecionada) return alert("Selecione a obra.");
    if (!selecionados.length) return alert("Selecione pelo menos um ensaio.");
    if (!certificadoSelecionado?.url) return alert("Selecione o equipamento com certificado de calibração.");

    const dados = {
      obraId: obraSelecionada.id,
      obraNome: obraSelecionada.nome || "",
      clienteId: obraSelecionada.clienteId || "",
      clienteNome: obraSelecionada.clienteNome || obraSelecionada.cliente || selecionados[0]?.cliente || "",
      ensaioIds: selecionados.map((item) => item.id),
      ensaiosResumo: selecionados.map((item) => ({ id: item.id, codigo: item.codigo || "", dataEnsaio: item.dataEnsaio || "" })),
      certificado: certificadoSelecionado,
      data: dataAtualISO(),
      criadoEm: new Date(),
    };
    const salvo = await addDoc(collection(db, COLECAO_RELATORIOS), aplicarTenantAtual(dados));
    setRelatorios((atual) => [{ id: salvo.id, ...dados }, ...atual]);
    setDialogAberto(false);
    abrirRelatorio({ id: salvo.id, ...dados });
  }

  async function abrirRelatorio(relatorio, imprimir = false) {
    const ensaios = (await Promise.all((relatorio.ensaioIds || []).map(async (id) => {
      const snap = await getDoc(doc(db, "tblModulosElasticidade", id));
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    }))).filter(Boolean);
    setModulosPrint(ordenarModulos(ensaios));
    setCertificadoPrint(relatorio.certificado || null);
    setDataRelatorioPrint(relatorio.data || "");
    setRelatorioPrint(relatorio);
    setAutoPrint(imprimir);
    setModoPrint(true);
  }

  async function vincularRelatorioAosLaudos(relatorio) {
    if (!relatorio?.id || !relatorio?.obraId) {
      throw new Error("Não foi possível identificar o relatório ou a obra.");
    }
    if (!auth.currentUser) throw new Error("Usuário não autenticado.");

    const laudosSnap = await getDocs(
      consultaTenantAtual(query(collection(db, "laudos"), where("obraId", "==", relatorio.obraId)))
    );
    const revisoes = laudosSnap.docs
      .map((item) => item.data())
      .filter((item) => item.relatorioModuloElasticidadeId === relatorio.id)
      .sort((a, b) => Number(b.numeroRevisao || 0) - Number(a.numeroRevisao || 0));
    const numeroRevisao = revisoes.length ? Number(revisoes[0].numeroRevisao || 0) + 1 : 0;
    const codigosEnsaios = (relatorio.ensaiosResumo || [])
      .map((item) => item.codigo)
      .filter(Boolean)
      .join(", ");
    const descricao = [`${codigosEnsaios || "Módulo de elasticidade"} rev.${numeroRevisao}`].join("");
    const nomeArquivo = `${nomeSeguroArquivo(descricao)}.pdf`;
    const storagePath = `laudos/${relatorio.obraId}/${Date.now()}-${nomeArquivo}`;
    const pdf = await gerarPdfRelatorioModuloElasticidade(certificadoPrint);

    await uploadBytes(ref(storage, storagePath), pdf, { contentType: "application/pdf" });
    const laudoRef = await addDoc(collection(db, "laudos"), aplicarTenantAtual({
      obraId: relatorio.obraId,
      tipo: TIPO_LAUDO_MODULO_ELASTICIDADE,
      descricao,
      nomeArquivo,
      storagePath,
      relatorioModuloElasticidadeId: relatorio.id,
      numeroRevisao,
      cancelado: false,
      criadoPor: auth.currentUser.uid,
      criadoPorEmail: auth.currentUser.email || "",
      criadoEm: serverTimestamp(),
    }));

    return { laudoId: laudoRef.id, numeroRevisao };
  }

  async function salvarPreviaAtual() {
    try {
      const salvo = await executarSalvamento(async () => {
        const revisaoSalva = await vincularRelatorioAosLaudos(relatorioPrint);
        await updateDoc(doc(db, COLECAO_RELATORIOS, relatorioPrint.id), {
          ...revisaoSalva,
          atualizadoEm: new Date(),
        });
        return { ...relatorioPrint, ...revisaoSalva };
      });

      if (salvo) {
        setRelatorioPrint(salvo);
        setRelatorios((atuais) => atuais.map((item) => item.id === salvo.id ? salvo : item));
        alert("Relatório salvo com sucesso!");
      }
    } catch (error) {
      console.error("Erro ao vincular relatório de módulo de elasticidade aos laudos:", error);
      alert("Não foi possível vincular o relatório aos laudos. Tente novamente.");
    }
  }

  if (modoPrint && modulosPrint.length) return (
    <RelatorioModuloElasticidadeMultiploPrint
      modulos={modulosPrint}
      certificado={certificadoPrint}
      dataRelatorio={dataRelatorioPrint}
      autoPrint={autoPrint}
      onSalvar={salvarPreviaAtual}
      salvando={salvandoCadastro}
      onVoltar={() => { setModoPrint(false); setModulosPrint([]); setCertificadoPrint(null); setDataRelatorioPrint(""); setRelatorioPrint(null); setAutoPrint(false); }}
    />
  );

  return <>
    <Navbar />
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Paper className="cabecalho-lista-cadastro" elevation={0}>
      <Typography variant="h6" fontWeight={700}>Relatórios de Módulo de Elasticidade</Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>Relatórios gerados para os ensaios cadastrados</Typography>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => history.push("/app/modulo-elasticidade")}>Voltar</Button>
        <Button variant="contained" onClick={abrirGeracao}>Gerar relatório</Button>
      </Stack>
      </Paper>
      <Paper sx={{ borderRadius: 2, overflow: "auto", border: "1px solid #e2e8f0", boxShadow: "none" }}>
        <Table className="lista-cadastro-padrao" size="small" sx={{ minWidth: 750 }}><TableHead sx={{ backgroundColor: "#f8fafc" }}><TableRow>
          <TableCell>Data</TableCell><TableCell>Obra</TableCell><TableCell>Ensaios</TableCell><TableCell align="center">Ações</TableCell>
        </TableRow></TableHead><TableBody>
          {relatorios.map((relatorio) => <TableRow key={relatorio.id} hover>
            <TableCell className="lista-dado-secundario">{formatarDataBR(relatorio.data)}</TableCell>
            <TableCell className="lista-dado-principal">{relatorio.obraNome || "-"}</TableCell>
            <TableCell className="lista-dado-destaque">{(relatorio.ensaiosResumo || []).map((item) => item.codigo || "-").join(", ") || "-"}</TableCell>
            <TableCell align="center"><Tooltip title="Visualizar"><IconButton size="small" onClick={() => abrirRelatorio(relatorio)}><VisibilityIcon color="primary" /></IconButton></Tooltip></TableCell>
          </TableRow>)}
          {!relatorios.length && <TableRow><TableCell colSpan={4} align="center">Nenhum relatório gerado.</TableCell></TableRow>}
        </TableBody></Table>
      </Paper>
    </Box>
    <Dialog open={dialogAberto} onClose={() => setDialogAberto(false)} fullWidth maxWidth="md">
      <DialogTitle>Gerar relatório de módulo de elasticidade</DialogTitle>
      <DialogContent dividers><Stack spacing={2} mt={1}>
        <Autocomplete options={obras} value={obraSelecionada} getOptionLabel={(item) => item?.nome || ""} isOptionEqualToValue={(a, b) => a.id === b.id} onChange={(event, value) => selecionarObra(value)} renderInput={(params) => <TextField {...params} label="Obra" placeholder="Selecione a obra" />} />
        <FormControlLabel control={<Checkbox checked={todos} onChange={(event) => alternarTodos(event.target.checked)} disabled={!obraSelecionada || !ensaiosDaObra.length} />} label="Todos" />
        <Typography variant="subtitle2">Ensaios da obra</Typography>
        <Paper variant="outlined" sx={{ maxHeight: 300, overflowY: "auto" }}>
          {!obraSelecionada && (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              Selecione uma obra para visualizar os ensaios.
            </Typography>
          )}
          {obraSelecionada && !ensaiosDaObra.length && (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              Nenhum ensaio cadastrado para esta obra.
            </Typography>
          )}
          {ensaiosDaObra.map((ensaio) => {
            const marcado = selecionados.some((item) => item.id === ensaio.id);

            return (
              <Box
                key={ensaio.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  px: 1,
                  "&:last-child": { borderBottom: 0 },
                }}
              >
                <Checkbox
                  checked={marcado}
                  onChange={(event) => alternarEnsaio(ensaio, event.target.checked)}
                  inputProps={{ "aria-label": `Selecionar ensaio ${ensaio.codigo || "sem código"}` }}
                />
                <Typography>
                  {`${ensaio.codigo || "-"} - ${formatarDataBR(ensaio.dataEnsaio)} - ${ensaio.localAplicacao || ""}`}
                </Typography>
              </Box>
            );
          })}
        </Paper>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
          <Autocomplete
            options={certificadosExibidos}
            value={certificadoSelecionado}
            getOptionLabel={(item) => item ? `${item.equipamentoNome} - ${item.numeroCertificado || item.nomeArquivoOriginal}` : ""}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(event, value) => setCertificadoSelecionado(value)}
            renderInput={(params) => <TextField {...params} label="Equipamento do certificado de calibração" />}
            sx={{ flex: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={mostrarTodosCertificados}
                onChange={(event) => {
                  setMostrarTodosCertificados(event.target.checked);
                  setCertificadoSelecionado(null);
                }}
              />
            }
            label="Mostrar todos"
          />
        </Stack>
      </Stack></DialogContent>
      <DialogActions><Button onClick={() => setDialogAberto(false)}>Cancelar</Button><Button variant="contained" onClick={gerarRelatorio}>Gerar relatório</Button></DialogActions>
    </Dialog>
  </>;
}

